// src/components/ReviewCard.js
import { useState } from 'react';

export default function ReviewCard({ item, schema, onApprove, onReject, onError }) {
  const suggested = item.suggested_features || {};
  const [features, setFeatures] = useState({ ...suggested });
  const [name, setName]         = useState(item.device_name || '');
  const [mfr, setMfr]           = useState(item.applicant || '');
  const [saving, setSaving]     = useState(false);
  const [expanded, setExpanded] = useState(true);

  const setFeature = (category, key, val) => {
    setFeatures(prev => ({
      ...prev,
      [category]: { ...(prev[category] || {}), [key]: val },
    }));
  };

  const handleApprove = async () => {
    if (!name.trim()) { onError('Implant name is required'); return; }
    setSaving(true);
    try {
      await onApprove(item.k_number, {
        implant_name: name.trim(),
        manufacturer: mfr.trim(),
        features,
      });
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm(`Reject ${item.k_number}?`)) return;
    try { await onReject(item.k_number); }
    catch (e) { onError(e.message); }
  };

  const featureCount = Object.values(features)
    .flatMap(cat => Object.values(cat || {}))
    .filter(Boolean).length;

  return (
    <div style={s.card}>
      {/* Header */}
      <div style={s.cardHeader} onClick={() => setExpanded(e => !e)}>
        <div style={s.cardHeaderLeft}>
          <span style={s.kNum}>{item.k_number}</span>
          <span style={s.arrow}>{expanded ? '▾' : '▸'}</span>
          <div>
            <div style={s.deviceName}>{item.device_name}</div>
            <div style={s.applicant}>{item.applicant} · cleared {item.decision_date}</div>
          </div>
        </div>
        <div style={s.cardHeaderRight}>
          <span style={s.featCount}>{featureCount} features mapped</span>
          {item.image_url && (
            <a href={item.image_url} target="_blank" rel="noreferrer" style={s.drawingLink}
               onClick={e => e.stopPropagation()}>
              📐 Drawing
            </a>
          )}
          {item.pdf_url && (
            <a href={item.pdf_url} target="_blank" rel="noreferrer" style={s.pdfLink}
               onClick={e => e.stopPropagation()}>
              📄 FDA PDF
            </a>
          )}
        </div>
      </div>

      {expanded && (
        <div style={s.cardBody}>
          {/* Error message from pipeline */}
          {item.error_message && (
            <div style={s.errorMsg}>⚠️ Pipeline note: {item.error_message}</div>
          )}

          {/* Drawing preview */}
          {item.image_url && (
            <div style={s.drawingWrap}>
              <img src={item.image_url} alt="implant drawing" style={s.drawing} />
            </div>
          )}

          {/* Editable name + manufacturer */}
          <div style={s.fieldRow}>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>Implant Name *</label>
              <input
                style={s.input}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Straumann BL SLActive"
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.fieldLabel}>Manufacturer</label>
              <input
                style={s.input}
                value={mfr}
                onChange={e => setMfr(e.target.value)}
                placeholder="e.g. Institut Straumann AG"
              />
            </div>
          </div>

          {/* Feature grid */}
          {schema && (
            <div style={s.featuresSection}>
              <div style={s.featuresTitle}>Features <span style={s.featHint}>(toggle to correct GPT-4o suggestions)</span></div>
              <div style={s.featureGrid}>
                {Object.entries(schema).map(([category, keys]) => (
                  <div key={category} style={s.featureCategory}>
                    <div style={s.catLabel}>{category.replace(/_/g, ' ')}</div>
                    <div style={s.catKeys}>
                      {keys.map(key => {
                        const on = !!(features[category]?.[key]);
                        return (
                          <button
                            key={key}
                            style={{ ...s.featureBtn, ...(on ? s.featureBtnOn : {}) }}
                            onClick={() => setFeature(category, key, !on)}
                          >
                            {on && <span style={s.checkmark}>✓ </span>}
                            {key.replace(/_/g, ' ')}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={s.actions}>
            <button style={s.rejectBtn} onClick={handleReject} disabled={saving}>
              Reject
            </button>
            <button style={s.approveBtn} onClick={handleApprove} disabled={saving}>
              {saving ? 'Saving…' : '✓ Approve & Add to Database'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  card: {
    background: '#111827', border: '1px solid #1e2530',
    borderRadius: 12, marginBottom: 16, overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', cursor: 'pointer', userSelect: 'none',
    borderBottom: '1px solid #1e2530',
  },
  cardHeaderLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  cardHeaderRight: { display: 'flex', alignItems: 'center', gap: 10 },
  kNum: {
    fontFamily: "'IBM Plex Mono', monospace", fontSize: 12,
    color: '#60a5fa', background: '#172554', padding: '3px 8px', borderRadius: 5,
    flexShrink: 0,
  },
  arrow: { color: '#475569', fontSize: 14 },
  deviceName: { fontSize: 14, fontWeight: 600, color: '#e2e8f0' },
  applicant:  { fontSize: 12, color: '#475569', marginTop: 2 },
  featCount: {
    fontSize: 11, color: '#94a3b8',
    fontFamily: "'IBM Plex Mono', monospace",
  },
  drawingLink: {
    fontSize: 12, color: '#a78bfa', textDecoration: 'none',
    padding: '3px 8px', background: '#1e1b4b', borderRadius: 5,
  },
  pdfLink: {
    fontSize: 12, color: '#60a5fa', textDecoration: 'none',
    padding: '3px 8px', background: '#172554', borderRadius: 5,
  },
  cardBody: { padding: 20 },
  errorMsg: {
    padding: '10px 14px', background: '#1c1107', border: '1px solid #78350f',
    borderRadius: 7, color: '#fbbf24', fontSize: 12, marginBottom: 16,
  },
  drawingWrap: {
    background: '#0a0d14', borderRadius: 8, padding: 12,
    marginBottom: 16, textAlign: 'center', border: '1px solid #1e2530',
  },
  drawing: { maxHeight: 220, maxWidth: '100%', objectFit: 'contain', filter: 'invert(0.9) contrast(1.1)' },
  fieldRow: { display: 'flex', gap: 16, marginBottom: 20 },
  fieldGroup: { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 },
  fieldLabel: { fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.07em' },
  input: {
    padding: '9px 12px', background: '#0a0d14', border: '1px solid #2d3748',
    borderRadius: 7, color: '#e2e8f0', fontSize: 13, fontFamily: "'Inter', sans-serif",
    outline: 'none',
  },
  featuresSection: { marginBottom: 20 },
  featuresTitle: { fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 12 },
  featHint: { fontWeight: 400, color: '#475569' },
  featureGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 },
  featureCategory: {},
  catLabel: {
    fontSize: 10, color: '#475569', textTransform: 'uppercase',
    letterSpacing: '0.08em', marginBottom: 6, fontWeight: 600,
  },
  catKeys: { display: 'flex', flexWrap: 'wrap', gap: 5 },
  featureBtn: {
    fontSize: 11, padding: '4px 9px', borderRadius: 5,
    border: '1px solid #2d3748', background: '#0a0d14',
    color: '#64748b', cursor: 'pointer', fontFamily: "'Inter', sans-serif",
    transition: 'all 0.1s',
  },
  featureBtnOn: {
    background: '#1e3a5f', border: '1px solid #1d4ed8',
    color: '#93c5fd',
  },
  checkmark: { color: '#60a5fa' },
  actions: { display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid #1e2530' },
  rejectBtn: {
    padding: '9px 20px', background: 'transparent', border: '1px solid #2d3748',
    borderRadius: 7, color: '#64748b', cursor: 'pointer', fontSize: 13,
    fontFamily: "'Inter', sans-serif",
  },
  approveBtn: {
    padding: '9px 24px', background: '#166534', border: '1px solid #15803d',
    borderRadius: 7, color: '#86efac', cursor: 'pointer', fontSize: 13,
    fontWeight: 600, fontFamily: "'Inter', sans-serif",
  },
};
